import React, { useEffect, useState, useMemo } from "react";
import AppLayout from "../../components/AppLayout";
import { api } from "../../api/client";
import {
  ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  Area, AreaChart, BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Users, TrendingUp, Award, BookOpen, BarChart3,
  Activity, Download, ChevronDown, Loader2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

// ─── shared chart theme ──────────────────────────────────────────────────────
const TIP  = { background: "#0E0F12", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, fontSize: 11, color: "#fff" };
const AXIS = { fill: "#6b7280", fontSize: 11 };
const GRID = { strokeDasharray: "3 3", stroke: "rgba(255,255,255,0.05)", vertical: false };
const PIE_COLORS = ["#CFFF50", "#7C5CFF", "#f472b6", "#22d3ee", "#facc15", "#fb923c", "#34d399"];

// ─── small reusable components ───────────────────────────────────────────────
function Card({ children, className = "" }) {
  return <div className={`bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5 ${className}`}>{children}</div>;
}

function StatCard({ icon: Icon, label, value, accent, sub }) {
  return (
    <Card>
      <Icon size={15} style={{ color: accent }} className="mb-3" />
      <div className="text-2xl font-black">{value ?? "—"}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
      {sub && <div className="text-[10px] text-gray-600 mt-0.5">{sub}</div>}
    </Card>
  );
}

function SectionTitle({ children }) {
  return <h2 className="text-lg font-bold mb-4">{children}</h2>;
}

function EmptyChart({ message = "No data yet" }) {
  return (
    <div className="flex items-center justify-center h-full text-gray-600 text-sm">{message}</div>
  );
}

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return d; } };

// ─── main page ───────────────────────────────────────────────────────────────
export default function AdminAnalytics() {
  const [data,       setData]      = useState(null);
  const [orgs,       setOrgs]      = useState([]);
  const [selectedOrg,setSelectedOrg] = useState("");
  const [orgReport,  setOrgReport] = useState(null);
  const [orgLoading, setOrgLoad]   = useState(false);
  const [loading,    setLoading]   = useState(true);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    Promise.all([
      api.get("/admin/analytics"),
      api.get("/admin/organisations").catch(() => []),
    ]).then(([a, o]) => {
      setData(a);
      setOrgs(Array.isArray(o) ? o : []);
    }).catch(() => toast.error("Failed to load analytics"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedOrg) { setOrgReport(null); return; }
    setOrgLoad(true);
    api.get(`/admin/organisations/${selectedOrg}/report`)
      .then(setOrgReport)
      .catch(() => toast.error("Failed to load org data"))
      .finally(() => setOrgLoad(false));
  }, [selectedOrg]);

  // ── org-derived data ────────────────────────────────────────────────────
  const orgOverdue = useMemo(() => {
    if (!orgReport) return [];
    const rows = [];
    for (const learner of orgReport) {
      for (const c of learner.courses) {
        if (c.status === "overdue") {
          const days = c.due_date
            ? Math.max(0, Math.floor((Date.now() - new Date(c.due_date)) / 86_400_000))
            : null;
          rows.push({ ...learner, course_title: c.course_title, progress: c.progress, due_date: c.due_date, days_overdue: days });
        }
      }
    }
    return rows;
  }, [orgReport]);

  const orgCourseChart = useMemo(() => {
    if (!orgReport) return [];
    const map: Record<string, { title: string; total: number; sum: number }> = {};
    for (const learner of orgReport) {
      for (const c of learner.courses) {
        if (!map[c.course_id]) map[c.course_id] = { title: c.course_title.slice(0, 24), total: 0, sum: 0 };
        map[c.course_id].total++;
        map[c.course_id].sum += c.progress;
      }
    }
    return Object.values(map).map((c) => ({ ...c, avg: c.total ? Math.round(c.sum / c.total) : 0 }));
  }, [orgReport]);

  const selectedOrgName = orgs.find((o) => o.id === selectedOrg)?.name ?? "";

  if (loading) return (
    <AppLayout>
      <div className="flex items-center justify-center py-24 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading analytics…
      </div>
    </AppLayout>
  );

  const s = data?.stats || {};

  return (
    <AppLayout searchPlaceholder="Search analytics...">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="text-xs uppercase tracking-widest text-[#7C5CFF] font-semibold">Insights</span>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-1">Analytics</h1>
        </div>

        {/* Org filter */}
        {orgs.length > 0 && (
          <div className="relative">
            <select
              value={selectedOrg}
              onChange={(e) => setSelectedOrg(e.target.value)}
              className="appearance-none bg-white/[0.04] border border-white/10 rounded-full pl-4 pr-9 py-2.5 text-sm font-medium text-white focus:outline-none focus:border-[#CFFF50]/40 cursor-pointer"
            >
              <option value="">Platform — All Orgs</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        )}
      </div>

      {/* ── Stat cards ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <StatCard icon={Users}     label="Total Learners"      value={s.total_learners}     accent="#CFFF50" />
        <StatCard icon={Activity}  label="Active This Month"   value={s.active_this_month}  accent="#7C5CFF" />
        <StatCard icon={TrendingUp}label="Total Completions"   value={s.total_completions}  accent="#CFFF50" />
        <StatCard icon={Award}     label="Certs This Month"    value={s.certs_this_month}   accent="#7C5CFF" />
        <StatCard icon={BookOpen}  label="Total Enrollments"   value={s.total_enrollments}  accent="#CFFF50" />
        <StatCard icon={BarChart3} label="Avg Completion Rate" value={`${s.avg_completion_rate ?? 0}%`} accent="#7C5CFF" />
      </div>

      {/* ── Revenue + Daily Logins ──────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <Card>
          <SectionTitle>Revenue</SectionTitle>
          <div className="h-56">
            {(data?.revenue || []).some((r) => r.v > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data?.revenue || []}>
                  <defs>
                    <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#CFFF50" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#CFFF50" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="m" tick={AXIS} axisLine={false} tickLine={false} />
                  <YAxis tick={AXIS} axisLine={false} tickLine={false} tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={TIP} formatter={(v) => [`£${v.toLocaleString()}`, "Revenue"]} />
                  <Area type="monotone" dataKey="v" stroke="#CFFF50" strokeWidth={2} fill="url(#gRev)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <EmptyChart message="No revenue data yet" />}
          </div>
        </Card>

        <Card>
          <SectionTitle>Daily Logins — last 14 days</SectionTitle>
          <div className="h-56">
            {(data?.logins_per_day || []).some((r) => r.v > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data?.logins_per_day || []}>
                  <defs>
                    <linearGradient id="gLog" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7C5CFF" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#7C5CFF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="day" tick={AXIS} axisLine={false} tickLine={false} interval={2} />
                  <YAxis tick={AXIS} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={TIP} formatter={(v) => [v, "Logins"]} />
                  <Area type="monotone" dataKey="v" stroke="#7C5CFF" strokeWidth={2} fill="url(#gLog)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <EmptyChart message="No login data yet" />}
          </div>
        </Card>
      </div>

      {/* ── Course completion rates ─────────────────────────────────────── */}
      <Card className="mb-5">
        <SectionTitle>Course Completion Rates</SectionTitle>
        <div className="h-64">
          {(data?.completion_rates || []).length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={data?.completion_rates || []} margin={{ left: 8, right: 24 }}>
                <CartesianGrid {...GRID} horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={AXIS} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="title" tick={{ ...AXIS, fontSize: 11 }} axisLine={false} tickLine={false} width={170} />
                <Tooltip contentStyle={TIP} formatter={(v, _, p) => [`${v}% (${p.payload.enrollments} enrolled)`, "Completion"]} />
                <Bar dataKey="rate" fill="#CFFF50" radius={[0, 6, 6, 0]} maxBarSize={20} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart message="No course enrollment data yet" />}
        </div>
      </Card>

      {/* ── Quiz pass rates + Video watch % ────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <Card>
          <SectionTitle>Quiz Pass Rates</SectionTitle>
          <div className="h-52">
            {(data?.quiz_pass_rates || []).length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.quiz_pass_rates || []} margin={{ bottom: 40 }}>
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="title" tick={{ ...AXIS, fontSize: 10 }} axisLine={false} tickLine={false} angle={-30} textAnchor="end" interval={0} />
                  <YAxis domain={[0, 100]} tick={AXIS} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={TIP} formatter={(v, _, p) => [`${v}% pass rate (${p.payload.attempts} attempts)`, "Pass Rate"]} />
                  <Bar dataKey="pass_rate" fill="#7C5CFF" radius={[6, 6, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart message="No quiz attempts yet" />}
          </div>
        </Card>

        <Card>
          <SectionTitle>Avg Video Watch %</SectionTitle>
          <div className="h-52">
            {(data?.watch_percentages || []).length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.watch_percentages || []} margin={{ bottom: 40 }}>
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="title" tick={{ ...AXIS, fontSize: 10 }} axisLine={false} tickLine={false} angle={-30} textAnchor="end" interval={0} />
                  <YAxis domain={[0, 100]} tick={AXIS} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={TIP} formatter={(v, _, p) => [`${v}% avg watch (${p.payload.records} sessions)`, "Watch %"]} />
                  <Bar dataKey="avg_watch" fill="#60a5fa" radius={[6, 6, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart message="No video progress data yet" />}
          </div>
        </Card>
      </div>

      {/* ── Top courses + Category mix ──────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <Card className="lg:col-span-2">
          <SectionTitle>Top Courses by Enrollment</SectionTitle>
          {(data?.top_courses || []).length === 0 ? (
            <div className="py-8 text-center text-gray-600 text-sm">No courses yet</div>
          ) : (
            <div className="space-y-2">
              {(data?.top_courses || []).map((c, i) => (
                <div key={c.id} className="flex items-center gap-3 bg-white/[0.02] rounded-xl p-3">
                  <span className="w-7 h-7 rounded-lg bg-lime/10 text-lime font-black text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                  {c.thumbnail && <img src={c.thumbnail} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{c.title}</div>
                    <div className="text-xs text-gray-500">{c.category}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-black text-base">{c.enrollments}</div>
                    <div className="text-xs text-gray-500">enrolled</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <SectionTitle>Category Mix</SectionTitle>
          <div className="h-56">
            {(data?.categories || []).length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data?.categories || []} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={70} stroke="none">
                    {(data?.categories || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 10, color: "#9CA3AF" }} />
                  <Tooltip contentStyle={TIP} />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyChart message="No courses yet" />}
          </div>
        </Card>
      </div>

      {/* ── Signups ─────────────────────────────────────────────────────── */}
      <Card className="mb-5">
        <SectionTitle>New Signups — last 8 weeks</SectionTitle>
        <div className="h-48">
          {(data?.signups || []).some((r) => r.v > 0) ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.signups || []}>
                <CartesianGrid {...GRID} />
                <XAxis dataKey="w" tick={AXIS} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={TIP} formatter={(v) => [v, "Signups"]} />
                <Bar dataKey="v" fill="#7C5CFF" radius={[6, 6, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart message="No signup data yet" />}
        </div>
      </Card>

      {/* ── Organisation view ───────────────────────────────────────────── */}
      {selectedOrg && (
        <div className="mt-2">
          {/* Org section header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="text-xs uppercase tracking-widest text-[#CFFF50] font-semibold">Organisation view</span>
              <h2 className="text-2xl font-black tracking-tight mt-0.5">{selectedOrgName}</h2>
            </div>
            <button
              onClick={() => window.open(`${API_BASE}/api/admin/organisations/${selectedOrg}/report.csv`, "_blank")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm font-semibold hover:bg-white/5 transition-colors"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>

          {orgLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading org data…
            </div>
          ) : (
            <>
              {/* Completion per course */}
              <Card className="mb-5">
                <SectionTitle>Completion by Course — {selectedOrgName}</SectionTitle>
                <div className="h-56">
                  {orgCourseChart.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart layout="vertical" data={orgCourseChart} margin={{ left: 8, right: 24 }}>
                        <CartesianGrid {...GRID} horizontal={false} />
                        <XAxis type="number" domain={[0, 100]} tick={AXIS} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                        <YAxis type="category" dataKey="title" tick={{ ...AXIS, fontSize: 11 }} axisLine={false} tickLine={false} width={160} />
                        <Tooltip contentStyle={TIP} formatter={(v) => [`${v}% avg`, "Progress"]} />
                        <Bar dataKey="avg" fill="#CFFF50" radius={[0, 6, 6, 0]} maxBarSize={22} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <EmptyChart message="No courses assigned to this organisation" />}
                </div>
              </Card>

              {/* Overdue training */}
              <Card>
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle size={16} className="text-red-400" />
                  <SectionTitle>Overdue Training</SectionTitle>
                </div>
                {orgOverdue.length === 0 ? (
                  <div className="text-center py-8 text-gray-600 text-sm">
                    {orgReport && orgReport.length > 0
                      ? "No overdue training — all learners are on track."
                      : "No learner data. Assign courses with due dates to track overdue training."}
                  </div>
                ) : (
                  <div className="border border-white/[0.08] rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/[0.08] text-xs text-gray-500 uppercase tracking-wider">
                          {["Learner","Course","Progress","Due Date","Days Overdue"].map((h) => (
                            <th key={h} className="text-left px-5 py-3 font-semibold">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/[0.05]">
                        {orgOverdue.map((row, i) => (
                          <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                            <td className="px-5 py-3">
                              <div className="font-semibold text-sm">{row.name}</div>
                              <div className="text-xs text-gray-500">{row.email}</div>
                            </td>
                            <td className="px-5 py-3 text-sm text-gray-300">{row.course_title}</td>
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden">
                                  <div className="h-full rounded-full bg-red-400" style={{ width: `${row.progress}%` }} />
                                </div>
                                <span className="text-xs text-gray-400">{row.progress}%</span>
                              </div>
                            </td>
                            <td className="px-5 py-3 text-xs text-gray-400">{fmtDate(row.due_date)}</td>
                            <td className="px-5 py-3">
                              <span className="text-xs font-bold text-red-400">
                                {row.days_overdue !== null ? `${row.days_overdue}d` : "—"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </>
          )}
        </div>
      )}

    </AppLayout>
  );
}
